/**
 * Papers the student's teachers set for them — one read behind every place a
 * student meets one: the bell, the banner on Today, and Tests → Assigned.
 *
 * Board and grade come from `useClaims()`, the reactive read, rather than a
 * `decodeClaims()` memoised once on mount: the bell lives in the app chrome,
 * which renders in the same commit as the guard that publishes that cache.
 *
 * "New" is derived, not stored: a paper the student has not started is one
 * they still need to hear about, and starting it is what clears it. So there
 * is no notification row to write, dedupe or forget to clear.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { useClaims } from '@/hooks/useSession';
import type { TeacherPaperSummaryDTO } from '@/types';

/** How often a visible tab re-checks, so a paper set during the day arrives without a reload. */
const ASSIGNED_REFRESH_MS = 60_000;

export function useAssignedPapers() {
  const claims = useClaims();
  const board = claims?.board ?? null;
  const grade = claims?.grade ?? null;

  const query = useQuery({
    queryKey: ['papers', 'teacher', board, grade],
    enabled: Boolean(board && grade),
    refetchInterval: ASSIGNED_REFRESH_MS,
    queryFn: () =>
      apiGet<TeacherPaperSummaryDTO[]>(`/papers?board=${board}&grade=${grade}&source=teacher`),
  });

  const papers = useMemo(() => query.data ?? [], [query.data]);
  // Newest first — the paper just set is the one to lead with.
  const unstarted = useMemo(
    () =>
      papers
        .filter((paper) => !paper.attempt)
        .sort((a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0)),
    [papers],
  );

  return { ...query, papers, unstarted };
}
